import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading, UitkText } from "@egds/react-core/text";
import { UitkSecondaryButton } from "@egds/react-core/button";
import { UitkLayoutGrid, UitkLayoutGridItem, UitkLayoutConditionalGridTrack } from "@egds/react-core/layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";
import { UitkSpacing } from "@egds/react-core/spacing";
import { FlexClickTracker } from "src/components/utility/analytics/FlexClickTracker/FlexClickTracker";

import { CarsFlexModuleResult } from "typings/microserviceModels/cars-flex-module";
import { CarOfferCard } from "./CarOfferCard";
import { CarsProps } from "./typings";
import { locKeys } from "./i10n";

const LOWEST_PRICE_CREATED_TIME_TOKEN_KEY = "carsLowestTotalPriceOfferCreatedTime";
const LOWEST_PRICE_UNITS_TOKEN_KEY = "carsLowestTotalPriceHoursOrDays";
const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

const gridColumnsConfig: UitkLayoutConditionalGridTrack = { small: 1, large: 2, extra_large: 3 };

const isShowDisclaimer = (model: CarsFlexModuleResult): boolean =>
  !!(model.tokens[LOWEST_PRICE_CREATED_TIME_TOKEN_KEY] && model.tokens[LOWEST_PRICE_UNITS_TOKEN_KEY]);

const showDisclaimerText = (
  model: CarsFlexModuleResult,
  formatText: (key: string, ...args: any[]) => string
): string => {
  const lowestPriceCreatedTime: string = model.tokens[LOWEST_PRICE_CREATED_TIME_TOKEN_KEY];
  const lowestPriceCreatedTimeUnit: string = model.tokens[LOWEST_PRICE_UNITS_TOKEN_KEY];

  return formatText(locKeys.disclaimerPrefix.concat(lowestPriceCreatedTimeUnit), lowestPriceCreatedTime);
};

export const Cars = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: CarsProps) => {
    const {
      context: { searchContext },
      templateComponent,
      flexModuleModelStore,
    } = props;

    const { formatText } = useLocalization();
    const { id } = templateComponent.metadata;
    const model = flexModuleModelStore.getModel(id) as CarsFlexModuleResult | null;
    const locationName = searchContext?.location?.name;

    if (!model) {
      return null;
    }

    const { title, carList, hideOptions, showSeeAll, seeAllUrl } = model;

    return (
      <UitkSpacing margin={{ block: "three" }}>
        <div className="Cars" id={id}>
          <UitkSpacing margin={{ blockend: "three" }}>
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
          </UitkSpacing>

          <UitkLayoutGrid space="three" columns={gridColumnsConfig}>
            {carList?.map((car) => (
              <UitkLayoutGridItem key={car.carModel}>
                <CarOfferCard carOffer={car} hideOptions={hideOptions} />
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>

          {showSeeAll && (
            <UitkLayoutFlex direction="column" alignItems="center">
              <UitkLayoutFlexItem>
                <UitkSpacing margin={{ blockstart: "six" }}>
                  <UitkHeading tag="h3" size={7}>
                    {formatText(locKeys.seeAllTitle)}
                  </UitkHeading>
                </UitkSpacing>
              </UitkLayoutFlexItem>

              {(searchContext.location || searchContext.carSupplierLocation) && (
                <UitkLayoutFlexItem>
                  <UitkText spacing="one" size={300}>
                    {formatText(
                      locKeys.seeAllSubtitle,
                      searchContext.location
                        ? searchContext.location.name
                        : searchContext.carSupplierLocation.address.firstAddressLine
                    )}
                  </UitkText>
                </UitkLayoutFlexItem>
              )}

              <UitkLayoutFlexItem>
                <UitkSpacing margin={{ blockstart: "three" }}>
                  <ButtonTracked
                    moduleName={"CarDeal"}
                    rfrr={`page.TravelGuides.Cars.${locationName}.ViewAllCars`}
                    linkName="LandingPageCarDeals"
                    tag="a"
                    href={seeAllUrl}
                  >
                    {formatText(locKeys.seeAllLink)}
                  </ButtonTracked>
                </UitkSpacing>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          )}
          {isShowDisclaimer(model) && (
            <UitkText spacing="three" size={100} align="center">
              {showDisclaimerText(model, formatText)}
            </UitkText>
          )}
        </div>
      </UitkSpacing>
    );
  })
);

export default Cars;
